import { useRef } from 'react';
import { Leaf, LockKey, ShieldCheck, UserFocus } from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { SECTION_IDS, site } from '../content/site';
import type { Principle } from '../content/site';
import { gsap, useGSAP, EASE, MOTION_OK } from '../lib/gsap';
import { SolarDisc } from '../components/principles/SolarDisc';
import { glue, noWidow } from '../components/vision/glue';

type Shape = 'wide' | 'tall' | 'small';

type CellStyle = {
	icon: Icon;
	shape: Shape;
	/** Grid placement. Mobile: 1 column. Tablet: 2 columns. Desktop: 6 columns. */
	span: string;
	/** Surface + primary ink. */
	surface: string;
	/** Secondary ink (AA on the surface). */
	soft: string;
	/** Hairline above the practice block. */
	rule: string;
	disc?: boolean;
};

/**
 * Gapless bento, 4 cells, grid-flow-dense.
 * Desktop (6 cols x 2 rows = 12 units): 4x1 + 2x2 + 2x1 + 2x1 = 12, no hole.
 *   [ Confidentialité 4 ][ Contrôle 2 ]
 *   [ Sécurité 2 ][ Sobriété 2 ][ (tall) ]
 * Tablet (2 cols x 3 rows = 6 units): 2x1 + 1x2 + 1x1 + 1x1 = 6, no hole.
 *   [ Confidentialité 2 ]
 *   [ Contrôle ][ Sécurité ]
 *   [ (tall)   ][ Sobriété ]
 * Contrast: Mineral on Forest (8.8:1), Graphite only on Sage (8.2:1), Graphite Muted
 * on Mist (5.2:1) and Mineral (5.9:1).
 */
const CELLS: Record<string, CellStyle> = {
	confidentialite: {
		icon: LockKey,
		shape: 'wide',
		span: 'md:col-span-2 lg:col-span-4',
		surface: 'bg-forest text-mineral',
		soft: 'text-mineral/80',
		rule: 'border-mineral/15',
	},
	controle: {
		icon: UserFocus,
		shape: 'tall',
		span: 'md:row-span-2 lg:col-span-2',
		surface: 'bg-sage text-graphite dark:bg-forest-deep dark:text-mineral',
		soft: 'text-graphite dark:text-dusk-muted',
		rule: 'border-graphite/15 dark:border-mineral/15',
	},
	securite: {
		icon: ShieldCheck,
		shape: 'small',
		span: 'lg:col-span-2',
		surface: 'bg-mist text-graphite dark:bg-forest-hover dark:text-mineral',
		soft: 'text-graphite-muted dark:text-mineral/80',
		rule: 'border-graphite/10 dark:border-mineral/15',
	},
	sobriete: {
		icon: Leaf,
		shape: 'small',
		span: 'lg:col-span-2',
		surface: 'border border-graphite/10 bg-mineral text-graphite dark:border-mineral/10 dark:bg-forest-night dark:text-mineral',
		soft: 'text-graphite-muted dark:text-dusk-muted',
		rule: 'border-graphite/10 dark:border-mineral/15',
		disc: true,
	},
};

const FALLBACK: CellStyle = CELLS.securite;

const STATEMENT_SIZE: Record<Shape, string> = {
	wide: 'text-[clamp(1.875rem,3.1vw,3.25rem)] max-w-[18ch]',
	tall: 'text-[clamp(1.875rem,3vw,3rem)] max-w-[13ch]',
	small: 'text-[clamp(1.625rem,2.2vw,2.25rem)] max-w-[16ch]',
};

/** Small cells get a floor height on desktop so the sun has room above the copy. */
const MIN_HEIGHT: Record<Shape, string> = {
	wide: '',
	tall: 'md:min-h-[34rem]',
	small: 'lg:min-h-[25rem]',
};

function PrincipleCell({ item, label }: { item: Principle; label: string }) {
	const style = CELLS[item.id] ?? FALLBACK;
	const Glyph = style.icon;
	const wide = style.shape === 'wide';

	return (
		<li data-cell className={`relative ${style.span}`}>
			<article
				className={`relative isolate flex h-full min-h-[19rem] flex-col overflow-hidden rounded-card p-7 sm:p-9 lg:p-10 ${style.surface} ${MIN_HEIGHT[style.shape]}`}
			>
				{style.disc && (
					<SolarDisc className="-right-[6.5rem] -top-[6.5rem] -z-10 w-[clamp(17rem,22vw,20rem)]" />
				)}

				<header className="flex items-center gap-3">
					<Glyph aria-hidden size={26} weight="regular" className="shrink-0" />
					<h3 className="font-display text-lg font-bold leading-none tracking-[-0.01em]">{item.title}</h3>
				</header>

				<div
					className={`mt-auto pt-14 ${
						wide ? 'lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:items-end lg:gap-x-12' : ''
					}`}
				>
					<p
						className={`text-balance font-display font-medium leading-[1.02] tracking-[-0.03em] ${STATEMENT_SIZE[style.shape]}`}
					>
						{glue(item.statement)}
					</p>
					<div className={`mt-8 border-t pt-5 ${style.rule} ${wide ? 'lg:mt-0' : ''}`}>
						<p className={`text-[13px] font-bold leading-none tracking-[0.01em] ${style.soft}`}>{label}</p>
						<p className={`mt-2.5 text-[15px] leading-relaxed ${style.soft}`}>{glue(item.practice)}</p>
					</div>
				</div>
			</article>
		</li>
	);
}

/**
 * Approche: how Ozan works, as a gapless 4-cell bento with varied surfaces.
 * The section paints no background of its own; the cells switch surfaces with the theme.
 * Dark contrast: Mineral on Forest (8.8:1) and Forest Deep / Forest Hover / Forest Night, Dusk Muted on
 * Forest Deep (7.5:1) and Forest Night (8.9:1), Mineral 80% on Forest Hover (6.9:1).
 */
export function Principles() {
	const ref = useRef<HTMLElement>(null);
	const { eyebrow, title, practiceLabel, items } = site.principles;

	useGSAP(
		() => {
			const section = ref.current;
			if (!section) return;
			const mm = gsap.matchMedia();

			mm.add(MOTION_OK, () => {
				const head = section.querySelector('[data-principles-head]');
				const grid = section.querySelector('[data-principles-grid]');

				if (head) {
					gsap.from(head.children, {
						y: 24,
						opacity: 0,
						duration: 1,
						ease: EASE,
						stagger: 0.08,
						scrollTrigger: { trigger: head, start: 'top 85%' },
					});
				}
				if (grid) {
					gsap.from(grid.querySelectorAll('[data-cell]'), {
						y: 30,
						opacity: 0,
						duration: 1.1,
						ease: EASE,
						stagger: 0.08,
						scrollTrigger: { trigger: grid, start: 'top 80%' },
					});
					// The sun settles into its rings once its cell is in place.
					gsap.from(grid.querySelectorAll('[data-sun]'), {
						yPercent: 28,
						duration: 1.8,
						ease: EASE,
						scrollTrigger: { trigger: grid, start: 'top 70%' },
					});
				}
			});
		},
		{ scope: ref },
	);

	return (
		<section
			id={SECTION_IDS.principles}
			ref={ref}
			aria-labelledby="principles-title"
			className="section-pad relative"
		>
			<div className="container-site">
				<div data-principles-head className="max-w-4xl">
					<p className="text-[13px] font-medium uppercase tracking-[0.16em] text-graphite-muted dark:text-dusk-muted">{eyebrow}</p>
					<h2
						id="principles-title"
						className="mt-6 text-balance font-display text-[clamp(2.25rem,4.6vw,4.5rem)] font-bold leading-[1] tracking-[-0.035em] text-graphite dark:text-mineral"
					>
						{noWidow(title)}
					</h2>
				</div>

				<ul
					data-principles-grid
					className="mt-14 grid grid-flow-dense grid-cols-1 gap-3 md:mt-20 md:grid-cols-2 md:gap-4 lg:grid-cols-6"
				>
					{items.map((item) => (
						<PrincipleCell key={item.id} item={item} label={practiceLabel} />
					))}
				</ul>
			</div>
		</section>
	);
}
